import {useEffect, useState, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {
  Button,
  Drawer,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from 'antd';
import {CheckOutlined, CloseOutlined, HistoryOutlined} from '@ant-design/icons';
import type {DailyExerciseReviewItem} from '@PikaDoApp/npm-pikado-logic';

import {DailyExercisesReviewService} from './DailyExercisesReviewService';
import {DailyExerciseStatusTag} from './DailyExerciseStatusTag';
import {DailyExerciseHistory} from './DailyExerciseHistory';
import {DailyExerciseSearch} from '../Test/DailyExerciseSearch';

const {Title} = Typography;
const {TextArea} = Input;

const reviewService = new DailyExercisesReviewService();

export const DailyExercisesReviewTable = () => {
  const [exercises, setExercises] = useState<DailyExerciseReviewItem[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [actioningId, setActioningId] = useState<string | null>(null);

  // Case-sensitive name search over the loaded review queue
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Reject modal state
  const [rejectTarget, setRejectTarget] =
    useState<DailyExerciseReviewItem | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');

  // History drawer state
  const [historyTarget, setHistoryTarget] =
    useState<DailyExerciseReviewItem | null>(null);

  const loadPage = useCallback(async (page: number) => {
    setIsLoading(true);
    try {
      const result = await reviewService.getExercisesForReview(page, 20);
      setExercises(result.items);
      setPagination({
        current: result.page,
        pageSize: 20,
        total: result.total,
      });
      setIsError(false);
    } catch (error) {
      console.error('Error fetching exercises for review ->', error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const handleTableChange = (paginationConfig: any) => {
    // While searching, the table paginates the filtered list client-side
    if (searchTerm) return;
    loadPage(paginationConfig.current);
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredExercises = searchTerm
    ? exercises.filter(e => (e.name || '').includes(searchTerm))
    : exercises;

  const handleApprove = async (id: string) => {
    setActioningId(id);
    try {
      await reviewService.approve(id);
      message.success('Daily exercise approved');
      await loadPage(pagination.current);
    } catch (error) {
      console.error('Approve error:', error);
      message.error('Failed to approve daily exercise');
    } finally {
      setActioningId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    const id = rejectTarget._id;
    setActioningId(id);
    try {
      await reviewService.reject(id, rejectReason.trim() || undefined);
      message.success('Daily exercise rejected');
      setRejectTarget(null);
      setRejectReason('');
      await loadPage(pagination.current);
    } catch (error) {
      console.error('Reject error:', error);
      message.error('Failed to reject daily exercise');
    } finally {
      setActioningId(null);
    }
  };

  const columns: any = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name: string, record: DailyExerciseReviewItem) => (
        <Link to={'/tests/' + record._id}>{name || record._id}</Link>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
    },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      render: (createdBy: string) => createdBy || '—',
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a: DailyExerciseReviewItem, b: DailyExerciseReviewItem) =>
        (a.createdAt || '').localeCompare(b.createdAt || ''),
      render: (value: string) =>
        value ? new Date(value).toLocaleString() : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: string) => <DailyExerciseStatusTag status={status} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: DailyExerciseReviewItem) => (
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={actioningId === record._id}
            onClick={() => handleApprove(record._id)}>
            Approve
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            disabled={actioningId === record._id}
            onClick={() => {
              setRejectReason('');
              setRejectTarget(record);
            }}>
            Reject
          </Button>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => setHistoryTarget(record)}>
            History
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{padding: 20}}>
      <Title level={3}>Daily Exercises Review</Title>

      {isError && <Title level={5}>Review queue fetching error ⚠️</Title>}

      {isLoading ? (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      ) : (
        <>
          <DailyExerciseSearch
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />
          <Table
            pagination={searchTerm ? {pageSize: 20} : pagination}
            columns={columns}
            dataSource={filteredExercises.map(e => ({...e, key: e._id}))}
            rowKey={record => record._id}
            onChange={handleTableChange}
          />
        </>
      )}

      <Modal
        title="Reject daily exercise"
        open={!!rejectTarget}
        onOk={submitReject}
        confirmLoading={!!actioningId && actioningId === rejectTarget?._id}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason('');
        }}
        okText="Reject"
        okButtonProps={{danger: true}}>
        <p>
          Rejecting <strong>{rejectTarget?.name || rejectTarget?._id}</strong>.
          Optionally provide a reason:
        </p>
        <TextArea
          rows={3}
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="Reason for rejection (optional)"
        />
      </Modal>

      <Drawer
        title={`History — ${historyTarget?.name || historyTarget?._id || ''}`}
        width={420}
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}>
        <DailyExerciseHistory history={historyTarget?.history} />
      </Drawer>
    </div>
  );
};
